/**
 * The expiry digest — deciding what is worth emailing a clinic about.
 *
 * ## Why this is not just "everything expiring"
 *
 * An alert that arrives every day saying the same thing stops being read within
 * a week, and then the one that matters is missed too. The rules below all
 * exist to make the email worth opening:
 *
 *   - Nothing to say means **no email**. A digest that arrives saying "nothing
 *     is expiring" trains people to delete it unopened.
 *   - Batches with **no stock left** are ignored. An empty batch cannot expire
 *     into a loss, and including them makes a clinic chase a number that costs
 *     nothing.
 *   - Already-expired stock leads, because it is money already lost and the
 *     only remaining action is to write it off.
 *
 * No I/O and no ambient time, per CLAUDE.md rules 3 and 4 — `today` is passed.
 */
import { z } from 'zod';
import { expiryStatusFor, stockStatus } from './status.js';
/** How far ahead the digest looks, in days. Matches the "soon" bucket. */
export const DIGEST_LEAD_DAYS = 84;
function daysBetween(from, to) {
    const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
    const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
    return Math.round((b - a) / 86_400_000);
}
/**
 * Builds one account's digest.
 *
 * `leadDays` is how far ahead to look. It is a parameter rather than a constant
 * because the spec has clinics choosing 30–90 days, and that setting will exist
 * before this function changes.
 */
export function buildExpiryDigest(batches, today, leadDays = DIGEST_LEAD_DAYS, products = []) {
    const expired = [];
    const urgent = [];
    const soon = [];
    for (const b of batches) {
        // No stock, or no expiry date, means nothing can be lost here.
        if (b.qtyRemaining <= 0 || !b.expiry)
            continue;
        const daysLeft = daysBetween(today, b.expiry);
        // Beyond the window is not silence — it is simply not urgent yet.
        if (daysLeft > leadDays)
            continue;
        const status = expiryStatusFor(b.expiry, today).key;
        if (status !== 'expired' && status !== 'urgent' && status !== 'soon')
            continue;
        const line = {
            productName: b.productName,
            lot: b.lot,
            expiry: b.expiry,
            qty: b.qtyRemaining,
            valuePence: b.qtyRemaining * b.unitCostPence,
            status,
            daysLeft,
        };
        if (status === 'expired')
            expired.push(line);
        else if (status === 'urgent')
            urgent.push(line);
        else
            soon.push(line);
    }
    // Soonest first within each group: the order someone should act in.
    const bySoonest = (a, b) => a.daysLeft - b.daysLeft;
    expired.sort(bySoonest);
    urgent.sort(bySoonest);
    soon.sort(bySoonest);
    const all = [...expired, ...urgent, ...soon];
    // At or below the minimum: the products the app shows with a "Reorder" or
    // "Out of stock" pill (`stockStatus`), so the email cannot name a product the
    // app shows as fine. Deliberately NARROWER than the Dashboard's "Reorder now"
    // count, which follows the prototype and also includes "Low" (up to twice the
    // minimum) — an email should ask for action, not for attention. A minimum of 0
    // means "never warn me about this one" — an out-of-stock product with no
    // minimum is one the clinic has stopped carrying, not one to reorder.
    const reorder = products
        .filter((p) => p.minLevel > 0)
        .filter((p) => {
        const key = stockStatus(p.qtyOnHand, p.minLevel).key;
        return key === 'out' || key === 'reorder';
    })
        .map((p) => ({
        productName: p.productName,
        qtyOnHand: p.qtyOnHand,
        minLevel: p.minLevel,
        out: p.qtyOnHand <= 0,
    }))
        // Out of stock first, then by name: the order someone should act in.
        .sort((a, b) => Number(b.out) - Number(a.out) || a.productName.localeCompare(b.productName));
    return {
        expired,
        urgent,
        soon,
        reorder,
        totalAtRiskPence: all.reduce((sum, l) => sum + l.valuePence, 0),
        /**
         * Only "soon" on its own is not worth an email — twelve weeks is not news,
         * and a weekly message about it is how a clinic learns to ignore the sender.
         * Something expired or going within four weeks is.
         */
        worthSending: expired.length > 0 || urgent.length > 0 || reorder.length > 0,
    };
}
/** One line of summary, used as the email subject. */
export function digestSubject(digest, clinicName) {
    const parts = [];
    if (digest.expired.length > 0)
        parts.push(`${digest.expired.length} expired`);
    if (digest.urgent.length > 0)
        parts.push(`${digest.urgent.length} expiring soon`);
    if (digest.reorder.length > 0)
        parts.push(`${digest.reorder.length} to reorder`);
    return `${clinicName}: ${parts.join(', ')}`;
}
// ------------------------------------------------------------ who gets it, when
/**
 * How often one person wants the digest. Per PERSON, not per clinic: the owner
 * who orders stock and the manager who is only copied in want different things,
 * and an unsubscribe that silenced a colleague would be a bug with a complaint
 * attached. It is also what AWS was told in the SES production-access appeal.
 *
 * `weekly` is the default. Expired stock stays expired until it is written off,
 * so `daily` repeats itself to anyone who has not acted — fine when chosen,
 * a reason to hit "spam" when imposed.
 */
export const DIGEST_FREQUENCIES = ['off', 'weekly', 'daily'];
export const DEFAULT_DIGEST_FREQUENCY = 'weekly';
export const DIGEST_FREQUENCY_LABEL = {
    off: 'Off',
    weekly: 'Weekly',
    daily: 'Daily',
};
export const notificationSettingsSchema = z.object({
    digestFrequency: z.enum(DIGEST_FREQUENCIES),
});
/** Monday. The start of the ordering week, and the day the weekly digest goes. */
export const WEEKLY_DIGEST_WEEKDAY = 1;
/**
 * Should this person be emailed on `today`? The job runs every day; this is
 * what turns that into each person's own cadence.
 *
 * `today` is the ACCOUNT's local date, so "Monday" means the clinic's Monday.
 * The weekday comes from the date's own digits via UTC, never from the server's
 * clock or zone — `new Date('2026-06-15')` read in local time is Sunday evening
 * west of Greenwich.
 */
export function digestDueOn(frequency, today) {
    if (frequency === 'off')
        return false;
    if (frequency === 'daily')
        return true;
    const [y, m, d] = today.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay() === WEEKLY_DIGEST_WEEKDAY;
}
//# sourceMappingURL=digest.js.map